import { useCallback, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { usePlayer } from '../lib/player'
import { usePlatform } from '../lib/platform'
import { toTitleCase } from '../lib/format'
import { reachBadge } from '../lib/reach'
import { Shell } from '../components/Shell'
import PortalFrame from '../components/PortalFrame'
import { Sill, Spinner, Check, MiniSpin, initials, avatarTone, personWash } from '../components/ui'
import SongRail from '../components/SongRail'
import AvatarUpload from '../components/AvatarUpload'
import SkillPicker from '../components/SkillPicker'
import { useSongDelete } from '../lib/songDelete'

/**
 * This screen used to be <Shell tabs> unconditionally — the phone chrome, on
 * every device. Every other staff screen already picks PortalFrame once the
 * viewport is past phone width; this was the one place someone coming from
 * the People/Setup dashboard hit a narrower, different-feeling page for no
 * reason tied to the content. Headings stay inline (this page writes its own,
 * unlike People's plain title prop) so the swap is chrome only.
 */
function Frame({ isPhone, children }) {
  return isPhone ? <Shell tabs>{children}</Shell> : <PortalFrame>{children}</PortalFrame>
}

const LEVELS = {
  learning: 'Learning it',
  comfortable: 'Comfortable',
  lead: 'Could lead it Sunday',
}

const STATUS = {
  draft: 'Draft',
  community_published: 'In the Collective',
  awaiting_cosign: 'Waiting on signatures',
  ready_to_send: 'Ready to send',
  in_review: 'With your church',
  closer_look: 'A closer look',
  paired: 'Paired up',
  rework: 'Back for a rework',
}

// Full names for the reader; the column itself stores the short code.
const STATE_NAMES = {
  NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', WA: 'Western Australia',
  SA: 'South Australia', TAS: 'Tasmania', ACT: 'Australian Capital Territory', NT: 'Northern Territory',
}

function Tile({ title, hint, children, onEdit, done }) {
  return (
    <div className="tile">
      <div className="tile-head">
        <div>
          <h3>{title}</h3>
          {hint && <p className="small">{hint}</p>}
        </div>
        {done
          ? <span className="tile-tick"><Check /></span>
          : <button className="iconbtn" onClick={onEdit}>Add</button>}
      </div>
      {children}
    </div>
  )
}

/** How long the recording on this draft has left. */
function mediaClock(song) {
  if (!song.r2_object_key || !song.media_expires_at) return null
  if (song.finished_at || song.submitted_at) return null
  const mins = Math.round((new Date(song.media_expires_at) - Date.now()) / 60000)
  if (mins <= 0) return 'recording expiring now'
  if (mins < 60) return `recording goes in ${mins} min`
  return 'recording goes in an hour'
}

/**
 * How long an unfinished clip has left in the feed.
 *
 * Separate from mediaClock: that one is about the file going while the song
 * stays, this is about the whole thing going unless the writer makes it a
 * song. Cleared the moment they write any lyrics.
 */
function clipClock(song) {
  if (!song.clip_expires_at) return null
  const mins = Math.round((new Date(song.clip_expires_at) - Date.now()) / 60000)
  if (mins <= 0) return 'clip going now — write it up to keep it'
  if (mins < 60) return `clip goes in ${mins} min unless you write it up`
  return `clip goes in ${Math.round(mins / 60)} h unless you write it up`
}

/**
 * The single status pill on a row. Three answers, one each:
 * still being written (Draft), sent somewhere and out of your hands
 * (Travelling), or finished and sitting exactly where you left it (Private,
 * or however far you've opened it).
 */
function statusPill(s) {
  if (!s.finished_at && !s.submitted_at) return <span className="pill">Draft</span>
  if (s.submitted_at) return <span className="pill accent">Travelling</span>
  if (s.reach === 'private' || !s.reach) return <span className="pill">Private</span>
  // The badge alone. "Open to The Collective" ran wider than the title it sat
  // beside, which inverted the row's hierarchy — the label outshouting the
  // song. The pill's presence already means "open this far".
  return <span className="pill accent">{reachBadge(s.reach)}</span>
}

/** Cut at a word, not mid-syllable. Ellipsis only when something was lost. */
function excerpt(text, max = 44) {
  const clean = text.trim()
  if (clean.length <= max) return clean
  const cut = clean.slice(0, max)
  return `${cut.slice(0, cut.lastIndexOf(' ')) || cut}…`
}

export default function Me() {
  const { user, profile, refresh, church, role, isPlatformOnly } = useSession()
  const { device } = usePlatform()
  const isPhone = device === 'phone'
  const nav = useNavigate()
  const [skills, setSkills] = useState([])
  const [avatarUrl, setAvatarUrl] = useState(null)
  // The stat row doubles as in-page navigation, same as on WriterProfile.
  const songsRef = useRef(null)
  const followersRef = useRef(null)
  const scrollTo = (r) => r.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  const [mine, setMine] = useState(null)
  const [onOthers, setOnOthers] = useState([])
  const [editing, setEditing] = useState(null) // about | skills
  const [form, setForm] = useState({ tagline: '', location: '', bio: '', state: '' })
  const [followers, setFollowers] = useState([])
  const [ready, setReady] = useState(false)
  const [err, setErr] = useState(null)
  const [loadingId, setLoadingId] = useState(null)
  const [srcMap, setSrcMap] = useState({})
  const [archivingId, setArchivingId] = useState(null)
  const player = usePlayer()

  const loadAvatar = useCallback(async (key) => {
    if (!key) return setAvatarUrl(null)
    const { data } = await supabase.functions.invoke('r2-avatar-url', {
      body: { mode: 'read', user_id: user.id },
    })
    setAvatarUrl(data?.url ?? null)
  }, [user])

  const load = useCallback(async () => {
    const [{ data: sk }, { data: songs, error }, { data: fol }] = await Promise.all([
      supabase.from('user_skills')
        .select('skill_id, level, skill_catalog(label, kind, position)')
        .eq('user_id', user.id),
      supabase.from('songs')
        .select('*, song_cowriters(id, cowriter_name, split_percentage, signed, user_id, is_primary)')
        .order('created_at', { ascending: false }),
      // Own-end-of-the-row RLS lets this read; the count is private to this
      // screen — nowhere else joins on writer_id = someone else's id.
      supabase.from('follows')
        .select('follower_id, created_at, users:follower_id(id, first_name, last_name)')
        .eq('writer_id', user.id)
        .order('created_at', { ascending: false }),
    ])
    if (error) setErr(readableError(error))
    setSkills(sk ?? [])
    setFollowers(fol ?? [])
    setMine((songs ?? []).filter((s) => s.songwriter_id === user.id))
    setOnOthers(
      (songs ?? []).filter(
        (s) => s.songwriter_id !== user.id &&
          s.song_cowriters?.some((c) => c.user_id === user.id && !c.signed)
      )
    )
    setReady(true)
  }, [user])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!profile) return
    setForm({
      tagline: profile.tagline || '', location: profile.location || '', bio: profile.bio || '',
      state: profile.state || '',
    })
    loadAvatar(profile.avatar_object_key)
  }, [profile, loadAvatar])

  const saveAbout = async () => {
    setErr(null)
    const { error } = await supabase.from('users').update({
      tagline: form.tagline.trim() || null,
      location: form.location.trim() || null,
      bio: form.bio.trim() || null,
      state: form.state || null,
    }).eq('id', user.id)
    if (error) return setErr(readableError(error))
    await refresh()
    setEditing(null)
  }

  const togglePlay = async (s) => {
    // Already the loaded track — let the provider toggle it rather than
    // re-fetching a URL we already have.
    if (player.isCurrent(s.id)) return player.toggle()
    let url = srcMap[s.id]
    if (!url) {
      setLoadingId(s.id)
      const { data, error } = await supabase.functions
        .invoke('r2-playback-url', { body: { song_id: s.id } })
      setLoadingId(null)
      if (error) return setErr(readableError(error))
      url = data?.url ?? null
      setSrcMap((m) => ({ ...m, [s.id]: url }))
    }
    if (!url) return
    player.play({
      id: s.id, src: url, mime: s.media_mime,
      title: s.title || 'Untitled', duration: s.duration_seconds,
    })
  }

  // The RPC refuses if any church still holds a live licence, so the error it
  // returns is the explanation — no need to duplicate that rule here.
  const { ask: askDelete, dialog: deleteDialog } = useSongDelete(load)

  const toggleArchive = async (s) => {
    setArchivingId(s.id)
    setErr(null)
    const { error } = await supabase.rpc('set_song_archived', {
      p_song_id: s.id, p_archived: !s.archived_at,
    })
    setArchivingId(null)
    if (error) return setErr(readableError(error))
    load()
  }

  if (!ready || !profile) return <Frame isPhone={isPhone}><Spinner /></Frame>

  const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ')
  const instruments = skills.filter((s) => s.skill_catalog?.kind === 'instrument')
  const competencies = skills.filter((s) => s.skill_catalog?.kind === 'competency')

  if (editing === 'about') return (
    <Frame isPhone={isPhone}>
      <button className="backlink" onClick={() => setEditing(null)}>‹ Profile</button>
      <h2 className="q">About you.</h2>
      <p className="sub">This is what people read next to your song.</p>
      <div className="field">
        <label className="label">A line about you</label>
        <input className="input" value={form.tagline} maxLength={80}
               placeholder="Writing from the secret place since 2021"
               onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
      </div>
      <div className="field">
        <label className="label">Where you write from</label>
        <input className="input" value={form.location} placeholder="Melbourne, VIC"
               onChange={(e) => setForm({ ...form, location: e.target.value })} />
      </div>
      <div className="field">
        <label className="label">The longer version</label>
        <textarea className="ta" value={form.bio} placeholder="Psalms, mostly. Slowly."
                  onChange={(e) => setForm({ ...form, bio: e.target.value })} />
      </div>
      <div className="field">
        <label className="label">Where you are</label>
        <select className="input" value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}>
          <option value="">Prefer not to say</option>
          {Object.entries(STATE_NAMES).map(([code, full]) => (
            <option key={code} value={code}>{full}</option>
          ))}
        </select>
        <p className="small">
          Helps other writers and churches near you find your songs.
        </p>
      </div>
      <button className="btn btn-primary" onClick={saveAbout}>Save</button>
      {err && <p className="err">{err}</p>}
    </Frame>
  )

  if (editing === 'skills') return (
    <SkillPicker
      userId={user.id}
      chosen={skills}
      onDone={async () => { await load(); setEditing(null) }}
      onBack={() => setEditing(null)}
    />
  )

  /**
   * What the info icon opens: the three optional things a reader might want to
   * know, in one place. All still optional, and each still edits through the
   * screen that already owned it.
   */
  if (editing === 'detail') {
    const skillTile = (title, hint, list) => (
      <Tile title={title} hint={hint} done={list.length > 0}
            onEdit={() => setEditing('skills')}>
        {list.length > 0 && (
          <>
            <div className="skillwrap">
              {list.map((s) => (
                <span key={s.skill_id} className={`skill lv-${s.level}`}>
                  {s.skill_catalog.label}<em>{LEVELS[s.level]}</em>
                </span>
              ))}
            </div>
            <button className="btn-plain" style={{ paddingLeft: 0 }}
                    onClick={() => setEditing('skills')}>Edit ›</button>
          </>
        )}
      </Tile>
    )

    return (
      <Frame isPhone={isPhone}>
        <button className="backlink" onClick={() => setEditing(null)}>‹ Profile</button>
        <div className="eyebrow">About</div>
        <h2 className="q">{name || 'You'}.</h2>
        <p className="sub">
          All optional. It is what someone reads next to your song, and nothing
          here decides what you can do.
        </p>
        <Sill />

        <Tile title="About you" hint="Your line, where you write from"
              done={!!(profile.tagline || profile.bio)} onEdit={() => setEditing('about')}>
          {(profile.tagline || profile.bio) && (
            <>
              {profile.tagline && <p style={{ marginBottom: 4 }}>{profile.tagline}</p>}
              {profile.bio && <p className="small">{profile.bio}</p>}
              <button className="btn-plain" style={{ paddingLeft: 0 }}
                      onClick={() => setEditing('about')}>Edit ›</button>
            </>
          )}
        </Tile>

        {skillTile('What you play', 'Instruments, and how confidently', instruments)}
        {skillTile('Skills', 'Writing, production, engineering', competencies)}
      </Frame>
    )
  }

  /**
   * A song row is one link plus, on a finished song, its own controls beneath
   * — outside the link, since a button can't nest inside a button.
   */
  const row = (s) => {
    const total = s.song_cowriters?.reduce((a, c) => a + Number(c.split_percentage), 0) ?? 0
    const signed = s.song_cowriters?.filter((c) => c.signed).length ?? 0
    const count = s.song_cowriters?.length ?? 0
    const clock = mediaClock(s)
    const done = !!(s.submitted_at || s.finished_at)
    const isPlaying = player.isCurrent(s.id) && player.playing
    const isLoading = loadingId === s.id

    return (
      <div key={s.id} className="songrow">
        <button className="action col"
                onClick={() => nav(done ? `/songs/${s.id}/done` : `/songs/${s.id}/1`)}>
          <div className="row">
            <span>{toTitleCase(s.title) || 'Untitled'}</span>
            {statusPill(s)}
          </div>
          <small>
            {STATUS[s.status] || s.status}
            {!done && ` · ${signed}/${count} signed · splits ${total}%`}
            {s.decision_note && ` · "${excerpt(s.decision_note)}"`}
          </small>
          {clock && <small className="clock">⏱ {clock}</small>}
          {clipClock(s) && <small className="clock">⏱ {clipClock(s)}</small>}
          {s.media_purged_at && !s.r2_object_key && (
            <small className="clock">Recording cleared — add it again when you send this</small>
          )}
        </button>

        {done && (
          <div className="songtools">
            {s.r2_object_key && (
              <button className="iconbtn" onClick={() => togglePlay(s)}
                      aria-pressed={isPlaying} disabled={isLoading}
                      aria-busy={isLoading}>
                {isLoading && <MiniSpin />}
                {isLoading ? 'Loading' : isPlaying ? '⏸ Pause' : '▶ Listen'}
              </button>
            )}
            <button className="iconbtn" onClick={() => nav(`/songs/${s.id}/learn`)}>Chart</button>
            {/* Every recording this song has had. Worth a place in the list
                because the vault is where a writer goes back to a take, and a
                screen you can only reach from inside another screen is a
                screen nobody opens. */}
            <button className="iconbtn" onClick={() => nav(`/songs/${s.id}/takes`)}>Versions</button>
            {/* The song says "Ready to send" and, until now, offered no way to
                send it. submit_song() works — it issues the reference and the
                rehearsal licence — but its only control sat three screens deep
                behind a button worded about reach ("Choose who hears it"), so
                finished songs simply accumulated here unsent.

                Routes to Sharing rather than calling submit_song directly, and
                deliberately so: sending grants a church three months of
                rehearsal rights, and that disclosure lives on that screen. A
                one-tap send from a list would be a rights grant the writer
                never read. */}
            {s.finished_at && !s.submitted_at && (
              <button className="iconbtn" onClick={() => nav(`/songs/${s.id}/share`)}>
                Send for a decision
              </button>
            )}
            {/* Archive is only ever tidying — a song a church is licensed to
                use is refused here and has to go through withdrawal, which
                owes the church notice and a reason. */}
            <button className="iconbtn" disabled={archivingId === s.id}
                    onClick={() => toggleArchive(s)}>
              {archivingId === s.id ? 'Working…' : s.archived_at ? 'Unarchive' : 'Archive'}
            </button>
            {/* Drafts have had a delete since they got their own screen;
                anything finished but unsent had none, which after clips
                started posting straight to the Collective meant a clip could
                be put in the feed and never taken out again. Same thirty-day
                archive either way, and the same refusal on a song a church
                already has. */}
            {!s.submitted_at && (
              <button className="iconbtn" onClick={() => askDelete(s)}>Delete</button>
            )}
          </div>
        )}
      </div>
    )
  }

  // Archived songs leave every other list rather than sitting greyed out among
  // live work — that is the whole point of tidying them away. Nothing is
  // deleted; they come back from the section at the bottom.
  const live = mine.filter((s) => !s.archived_at)
  const drafts = live.filter((s) => !s.finished_at && !s.submitted_at)
  const finished = live.filter((s) => s.finished_at && !s.submitted_at)
  const sent = live.filter((s) => s.submitted_at)
  const archived = mine.filter((s) => s.archived_at)

  return (
    <Frame isPhone={isPhone}>
      {/* The same header the rest of the app gives a writer — see
          WriterProfile. Your own profile used to be the odd one out: a left
          aligned identity card with the avatar beside the name, so /me and
          /writer/:id were two different screens describing the same person.
          Centred face over the wash, then the readable half below it. */}
      <header className="wp-head">
        <div className="wp-wash" style={{ background: personWash(profile.id) }}
             aria-hidden="true" />
        <AvatarUpload profile={profile} url={avatarUrl}
                      onChange={async (key) => { await refresh(); loadAvatar(key) }} />
        <h2 className="wp-name">{name || 'Your name'}</h2>
        <p className="wp-tagline">
          {isPlatformOnly ? 'Platform staff' : 'Songwriter'}
          {profile.location ? ` · ${profile.location}` : ''}
        </p>
        {profile.tagline && <p className="wp-place">{profile.tagline}</p>}
        {profile.bio && <p className="wp-bio">{profile.bio}</p>}

        {/* Two actions, the way the profile shape wants them: the one you came
            here to do, and the one that edits the page you're looking at. */}
        <div className="wp-acts">
          {!isPlatformOnly && (
            <button className="btn btn-primary" onClick={() => nav('/songs/new')}>
              New song
            </button>
          )}
          <button className="btn btn-quiet" onClick={() => setEditing('detail')}>
            Edit profile
          </button>
        </div>
      </header>

      {/* Three numbers about you, each one a summary of something further down
          this page. Followers appear here and only here — see below. */}
      {!isPlatformOnly && (
        <div className="wp-stats">
          <button className="wp-stat" onClick={() => scrollTo(songsRef)}>
            <b>{mine.length}</b><span>Songs</span>
          </button>
          <button className="wp-stat" onClick={() => scrollTo(followersRef)}>
            <b>{followers.length}</b><span>Followers</span>
          </button>
          <button className="wp-stat" onClick={() => nav('/drafts')}>
            <b>{drafts.length}</b><span>Drafts</span>
          </button>
        </div>
      )}

      <div className="chips chips-centre" style={{ marginBottom: 22 }}>
        <span className="pill ok">Email verified</span>
        {church && <span className="pill">{church.name}</span>}
      </div>

      {/* The songwriter half of the profile. Platform staff have no catalog,
          no drafts and no co-writer requests, and showing them the empty
          version of all three — "0 songs", "Nothing here yet. The first one's
          the hardest." — describes a person who isn't reading it. */}
      {!isPlatformOnly && (
        <>
        {/* Your songs — moved here from a separate tab. This is your catalog,
            not a browsable feed, so drafts belong on the page only you see by
            default. */}
        <div className="rowhead" ref={songsRef}>
          <h3 className="sechead" style={{ margin: 0 }}>My songs</h3>
          {drafts.length > 0 && (
            <button className="btn-plain" style={{ minHeight: 0, padding: 0 }}
                    onClick={() => nav('/drafts')}>Drafts ›</button>
          )}
        </div>

        {onOthers.length > 0 && (
          <>
            {onOthers.map((s) => (
              <button key={s.id} className="action col waiting" style={{ marginTop: 10 }}
                      onClick={() => nav(`/songs/${s.id}/sign`)}>
                <div className="row"><span>{toTitleCase(s.title)}</span><span className="ar">›</span></div>
                <small>You're a co-writer · sign to release it</small>
              </button>
            ))}
          </>
        )}

        {finished.length === 0 && sent.length === 0 ? (
          <p className="small" style={{ marginTop: 10 }}>
            {drafts.length > 0
              ? 'Nothing finished yet — what you have is still in drafts.'
              : "Nothing here yet. The first one's the hardest."}
          </p>
        ) : (
          /* A catalogue, in the shape music is published in. The controls
             each song used to carry inline — Chart, Versions, Send, Archive —
             live on the song's own screen, which is where the card goes; four
             buttons under every row made the list a control panel and the
             song an afterthought. The pill still rides the card, because
             "Travelling" is the one thing you need without opening it. */
          <SongRail songs={[...finished, ...sent]}
                    to={(s) => `/collective/${s.id}`}
                    meta={(s) => STATUS[s.status] || s.status}
                    badge={statusPill} />
        )}

        {archived.length > 0 && (
          <>
            <div className="eyebrow" style={{ marginTop: 24 }}>Archived</div>
            <p className="small" style={{ marginTop: -4, marginBottom: 8 }}>
              Off your shelf, still yours. Nothing here has been deleted.
            </p>
            {archived.map(row)}
          </>
        )}

        {role && role !== 'songwriter' && (
          <p className="small" style={{ marginTop: 12 }}>
            You're signed in as {role} at {church?.name}. Your own songs live here.
          </p>
        )}
        </>
      )}

      {/* Followers are private to the person followed — this is the only
          screen that ever shows a count, and it's always your own. */}
      <h3 className="sechead" ref={followersRef}>Following you</h3>
      {followers.length === 0 ? (
        <p className="small" style={{ marginTop: -4 }}>
          No one yet — putting a clip in the Collective is how writers find you.
        </p>
      ) : (
        <>
          <p className="small" style={{ marginTop: -4 }}>
            {followers.length} {followers.length === 1 ? 'person follows' : 'people follow'} you
          </p>
          <div className="rows" style={{ marginTop: 8 }}>
            {followers.map((f) => {
              const fname = [f.users?.first_name, f.users?.last_name].filter(Boolean).join(' ') || 'A writer'
              return (
                <button key={f.follower_id} className="action"
                        onClick={() => nav(`/writer/${f.follower_id}`)}>
                  <span className={`ava ${avatarTone(f.follower_id)}`} aria-hidden="true">
                    {initials(fname)}
                  </span>
                  <span>{fname}</span>
                  <span className="ar">›</span>
                </button>
              )
            })}
          </div>
        </>
      )}

      {/* One group, not two rules with a lone row after each. These are the
          two doors off the profile and they read as a pair — the same shape
          the account screen behind the second one is built from. */}
      <h3 className="sechead">Settings</h3>
      <div className="rows">
        {!isPlatformOnly && (
          <button className="action" onClick={() => nav('/circles')}>
            <span>Circles<small>Who hears your songs, and whose reach you</small></span>
            <span className="ar">›</span>
          </button>
        )}
        <button className="action" onClick={() => nav('/account')}>
          <span>Account settings<small>Security, data, legal</small></span>
          <span className="ar">›</span>
        </button>
      </div>

      {err && <p className="err">{err}</p>}
      {deleteDialog}
    </Frame>
  )
}
